import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useDashboard } from "@/hooks/queries/dashboard/useDashboard";
import { Button, Skeleton } from "@/components/ui";

const currencyBRL = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

export function DashboardCharts() {
  const { charts, chartsLoading, refetchCharts } = useDashboard();

  if (chartsLoading) {
    return (
      <section className="col-span-full rounded-xl border border-app-border bg-app-surface p-6 shadow-premium-sm lg:col-span-3">
        <header className="mb-4">
          <h3 className="text-lg font-semibold tracking-tight text-app-fg-strong">
            Faturamento vs Interações IA
          </h3>
          <p className="mt-1 text-sm text-app-fg-muted">
            Carregando histórico...
          </p>
        </header>
        <Skeleton className="h-[300px] w-full rounded-lg" />
      </section>
    );
  }

  if (!charts || !charts.labels || charts.datasets.length < 2) return <section className="col-span-full space-y-3 rounded-xl border border-app-border bg-app-surface p-6 lg:col-span-3">
    <p role="status">Histórico de faturamento e interações indisponível.</p>
    <Button variant="outline" onClick={() => void refetchCharts()}>Atualizar histórico</Button>
  </section>;

  const data = charts.labels.map((label, index) => ({
    name: label,
    revenue: charts.datasets[0].data[index] ?? null,
    ai: charts.datasets[1].data[index] ?? null,
  }));

  return (
    <section className="col-span-full rounded-xl border border-app-border bg-app-surface p-6 shadow-premium-sm lg:col-span-3">
      <header className="mb-4">
        <h3 className="text-base font-semibold tracking-tight text-app-fg-strong">
          Faturamento vs Interações IA
        </h3>
        <p className="mt-1 text-sm text-app-fg-muted">
          Acompanhe o crescimento da receita em relação ao uso do Agente de IA nos últimos 6 meses.
        </p>
      </header>

      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--brand))" stopOpacity={0.25} />
                <stop offset="95%" stopColor="hsl(var(--brand))" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorAI" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--app-info))" stopOpacity={0.22} />
                <stop offset="95%" stopColor="hsl(var(--app-info))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: "hsl(var(--app-fg-muted))" }}
              dy={10}
            />
            <YAxis
              yAxisId="left"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: "hsl(var(--app-fg-muted))" }}
              tickFormatter={(value) =>
                `R$ ${value >= 1000 ? (value / 1000).toFixed(1) + "k" : value}`
              }
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: "hsl(var(--app-fg-muted))" }}
            />
            <CartesianGrid
              vertical={false}
              stroke="hsl(var(--app-border))"
              strokeDasharray="3 3"
            />
            <Tooltip
              contentStyle={{
                borderRadius: "8px",
                border: "1px solid hsl(var(--app-border))",
                backgroundColor: "hsl(var(--app-surface))",
                boxShadow: "0 8px 24px rgba(20,18,15,0.06)",
              }}
              formatter={(value: number, name: string) => {
                if (name === "revenue")
                  return [currencyBRL.format(value), "Faturamento"];
                return [value, "Interações IA"];
              }}
            />
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="revenue"
              stroke="hsl(var(--brand))"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorRevenue)"
            />
            <Area
              yAxisId="right"
              type="monotone"
              dataKey="ai"
              stroke="hsl(var(--app-info))"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorAI)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
